// web/src/lib/loaderBeam.ts
// Charge → loader-visual settings, KEYFRAMED per named beat (Idle → Analyze → Roast →
// Sauce → Serve → Ignition). Each stage holds the Unicorn beam variables + cover
// shake/glow; the values interpolate between stages by `charge`. Cover position/size is
// STATIC (not per-stage). All of it is a tunable external store (mirrors wordmarkFx) the
// DEV LoaderFxDebug panel dials live; production ships LOADER_FX_DEFAULTS. Every field maps
// to a variable that is actually BOUND in the scene (cleaned-up 2026-07-04 binding set).
import { useSyncExternalStore } from "react";

/** Exact display-names of the scene's 7 BOUND variables (setVariables keys), per the
 *  cleaned-up 2026-07-04 scene. Unbound/removed vars are intentionally NOT here. */
export const VAR_NAMES = {
  beamWidth: "Energy Tractor Beam Beam Width", // bound → tractor-beam width
  ripple: "Energy Tractor Beam Ripple Scale", // bound → tractor-beam ripple
  exposure: "Gemini Image Exposure", // bound → Gemini star brightness
  shake: "Earthquake Jitter Shake Magnitude", // bound → star shake magnitude (re-bound)
  swirlScale: "Swirl Beam Scale", // bound → swirl radius
  swirlSpeed: "Swirl Beam Speed 2", // bound → swirl speed
  circleMix: "Circle Mix", // bound → the green "circle" layer blend
} as const;

/** The per-stage keyframed settings (beam vars + cover shake/glow). */
export interface StageSettings {
  beamWidth: number; ripple: number; // tractor-beam vars
  exposure: number; shake: number; // Gemini star: brightness + shake magnitude
  swirlScale: number; swirlSpeed: number; // swirl-beam radius + speed
  circleMix: number; // green circle-layer blend
  coverShake: number; // 0–1 cover shake amplitude multiplier
  coverGlow: number; // 0–1 cover glow intensity
}
export const STAGE_FIELDS: (keyof StageSettings)[] = [
  "beamWidth", "ripple", "exposure", "shake",
  "swirlScale", "swirlSpeed", "circleMix",
  "coverShake", "coverGlow",
];

export interface LoaderStage { key: string; label: string; atCharge: number; }
/** Keyframe stages along charge 0→100 (the loader's named beats). */
export const LOADER_STAGES: readonly LoaderStage[] = [
  { key: "idle", label: "Idle", atCharge: 0 },
  { key: "analyze", label: "Analyze", atCharge: 18 },
  { key: "roast", label: "Roast", atCharge: 42 },
  { key: "sauce", label: "Sauce", atCharge: 66 },
  { key: "serve", label: "Serve", atCharge: 88 },
  { key: "ignition", label: "Ignition", atCharge: 100 },
] as const;

export interface LoaderFx {
  stages: Record<string, StageSettings>; // keyed by LoaderStage.key
  coverX: number; // px offset from horizontal center (static)
  coverY: number; // vertical position, viewport % (static)
  coverScale: number; // cover size scale (static)
  coverRotate: number; // cover tilt, degrees (static; Figma = -5)
}

// Tuned live via /dev/loader (2026-07-04). Field order: beamWidth, ripple, exposure, shake,
// swirlScale, swirlSpeed, circleMix, coverShake, coverGlow. shake = star jitter magnitude
// (bound); circleMix ramps the green base-circle in toward the climax.
const STAGE_DEFAULTS: Record<string, StageSettings> = {
  idle: { beamWidth: 0, ripple: 0, exposure: -0.09, shake: 0, swirlScale: 0, swirlSpeed: 0, circleMix: 0, coverShake: 0.1, coverGlow: 0 },
  analyze: { beamWidth: 0.42, ripple: 0.51, exposure: 0.05, shake: 0.02, swirlScale: 0.34, swirlSpeed: 0.3, circleMix: 0.1, coverShake: 0.2, coverGlow: 0.32 },
  roast: { beamWidth: 0.55, ripple: 0.6, exposure: 0.13, shake: 0.03, swirlScale: 0.4, swirlSpeed: 0.5, circleMix: 0.3, coverShake: 0.45, coverGlow: 0.5 },
  sauce: { beamWidth: 0.67, ripple: 0.68, exposure: 0.2, shake: 0.045, swirlScale: 0.45, swirlSpeed: 0.7, circleMix: 0.55, coverShake: 0.65, coverGlow: 0.65 },
  serve: { beamWidth: 0.79, ripple: 0.76, exposure: 0.26, shake: 0.055, swirlScale: 0.5, swirlSpeed: 0.9, circleMix: 0.8, coverShake: 0.85, coverGlow: 0.82 },
  ignition: { beamWidth: 1, ripple: 0.8, exposure: 0.22, shake: 0.07, swirlScale: 0.55, swirlSpeed: 1.1, circleMix: 1, coverShake: 1, coverGlow: 1 },
};

function cloneDefaults(): LoaderFx {
  return {
    stages: Object.fromEntries(LOADER_STAGES.map((s) => [s.key, { ...STAGE_DEFAULTS[s.key] }])),
    coverX: 0,
    coverY: 48, // centered on the beam's convergence so the cover catches the beam's end
    coverScale: 1,
    coverRotate: -5,
  };
}

export const LOADER_FX_DEFAULTS: LoaderFx = cloneDefaults();

/** The interpolated settings at a given charge. */
export interface LoaderSettings { beamVars: Record<string, number>; coverShake: number; coverGlow: number; }

/** charge 0–100 → interpolated stage settings (beam vars keyed by Unicorn display-name). */
export function settingsForCharge(charge: number, fx: LoaderFx): LoaderSettings {
  const c = Math.min(100, Math.max(0, charge));
  const stages = LOADER_STAGES;
  let lo = stages[0];
  let hi = stages[stages.length - 1];
  for (let i = 0; i < stages.length - 1; i++) {
    if (c >= stages[i].atCharge && c <= stages[i + 1].atCharge) { lo = stages[i]; hi = stages[i + 1]; break; }
  }
  const span = hi.atCharge - lo.atCharge || 1;
  const t = (c - lo.atCharge) / span;
  const a = fx.stages[lo.key] ?? STAGE_DEFAULTS[lo.key];
  const b = fx.stages[hi.key] ?? STAGE_DEFAULTS[hi.key];
  const L = (k: keyof StageSettings): number => a[k] + (b[k] - a[k]) * t;
  return {
    beamVars: {
      [VAR_NAMES.beamWidth]: L("beamWidth"),
      [VAR_NAMES.ripple]: L("ripple"),
      [VAR_NAMES.exposure]: L("exposure"),
      [VAR_NAMES.shake]: L("shake"),
      [VAR_NAMES.swirlScale]: L("swirlScale"),
      [VAR_NAMES.swirlSpeed]: L("swirlSpeed"),
      [VAR_NAMES.circleMix]: L("circleMix"),
    },
    coverShake: L("coverShake"),
    coverGlow: L("coverGlow"),
  };
}

/** The keyframe stage whose `atCharge` is nearest the given charge (for charge↔tab linking). */
export function nearestStageKey(charge: number): string {
  const c = Math.min(100, Math.max(0, charge));
  let best = LOADER_STAGES[0];
  for (const s of LOADER_STAGES) {
    if (Math.abs(s.atCharge - c) < Math.abs(best.atCharge - c)) best = s;
  }
  return best.key;
}

/** Back-compat alias: just the Unicorn beam variables at a charge (used by UnicornBeam). */
export function beamVarsForCharge(charge: number, fx: LoaderFx): Record<string, number> {
  return settingsForCharge(charge, fx).beamVars;
}

let _fx: LoaderFx = cloneDefaults();
const _subs = new Set<() => void>();
const emit = (): void => { for (const cb of _subs) cb(); };

export const loaderFx = {
  get: (): LoaderFx => _fx,
  /** Set static (cover layout) fields. */
  set: (patch: Partial<Omit<LoaderFx, "stages">>): void => { _fx = { ..._fx, ...patch }; emit(); },
  /** Patch one stage's keyframed settings. */
  setStage: (key: string, patch: Partial<StageSettings>): void => {
    _fx = { ..._fx, stages: { ..._fx.stages, [key]: { ..._fx.stages[key], ...patch } } };
    emit();
  },
  /** Copy one stage's settings onto every stage (a uniform baseline). */
  copyStageToAll: (key: string): void => {
    const src = _fx.stages[key];
    const stages = Object.fromEntries(LOADER_STAGES.map((s) => [s.key, { ...src }]));
    _fx = { ..._fx, stages };
    emit();
  },
  reset: (): void => { _fx = cloneDefaults(); emit(); },
  subscribe: (cb: () => void): (() => void) => { _subs.add(cb); return () => { _subs.delete(cb); }; },
};

/** Reactive read of the current loader FX settings. */
export function useLoaderFx(): LoaderFx {
  return useSyncExternalStore(loaderFx.subscribe, loaderFx.get, () => LOADER_FX_DEFAULTS);
}
